import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, X, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { FinancingNote } from "./shared";

const services = ["Commercial Soft-Wash", "Paver Sealing", "High-Pressure Washing", "Driveway & Concrete Cleaning", "Pool Area Cleaning", "Not sure — need advice"];
const contactMethods = ["Phone Call", "Text Message", "Email"];
const heardOptions = ["Google", "Facebook", "Instagram", "Referral", "Drove by", "Returning customer", "Other"];

const inputCls = "w-full h-12 px-4 rounded-xl border border-gray-200 bg-white text-[var(--text-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--action)] focus:border-transparent";
const labelCls = "block text-sm font-semibold text-[var(--navy)] mb-1.5";

export const QuoteForm = ({ variant = "general", source = "quote_form" }) => {
  const nav = useNavigate();
  const isCommercial = variant === "commercial";
  const [f, setF] = useState({
    first_name: "", last_name: "", phone: "", email: "", address: "", city: "Orlando", state: "FL", zip: "",
    property_type: isCommercial ? "commercial" : "residential", service: "", surface_type: "",
    description: "", preferred_contact: "", preferred_date: "", preferred_time: "", heard_about: "",
    additional_notes: "", consent: false, company_name: "", property_count: "", recurring: "", urgency: "",
  });
  const [photos, setPhotos] = useState([]); // {id, filename}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");

  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post("/uploads/quote-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { id: r.data.id, filename: r.data.filename }]);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Could not upload ${file.name}`);
      }
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    if (!f.first_name || !(f.phone || f.email)) { toast.error("Please add your name and a phone or email."); return; }
    if (!f.consent) { toast.error("Please provide consent to be contacted."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/leads", {
        ...f, services: f.service ? [f.service] : [], photo_ids: photos.map((p) => p.id),
        source, landing_page: window.location.pathname,
      });
      nav(`/quote-confirmation?id=${r.data.lead_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please call us at (321) 376-8744.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
      <input type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
             className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />

      {isCommercial && (
        <div>
          <label className={labelCls}>Company Name</label>
          <input className={inputCls} value={f.company_name} onChange={set("company_name")} data-testid="qf-company" />
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div><label className={labelCls}>First Name *</label>
          <input className={inputCls} value={f.first_name} onChange={set("first_name")} data-testid="qf-first" required /></div>
        <div><label className={labelCls}>Last Name</label>
          <input className={inputCls} value={f.last_name} onChange={set("last_name")} data-testid="qf-last" /></div>
        <div><label className={labelCls}>Phone *</label>
          <input className={inputCls} value={f.phone} onChange={set("phone")} data-testid="qf-phone" type="tel" /></div>
        <div><label className={labelCls}>Email</label>
          <input className={inputCls} value={f.email} onChange={set("email")} data-testid="qf-email" type="email" /></div>
      </div>

      <div>
        <label className={labelCls}>Property Address</label>
        <input className={inputCls} value={f.address} onChange={set("address")} data-testid="qf-address" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div><label className={labelCls}>City</label><input className={inputCls} value={f.city} onChange={set("city")} data-testid="qf-city" /></div>
        <div><label className={labelCls}>State</label><input className={inputCls} value={f.state} onChange={set("state")} /></div>
        <div><label className={labelCls}>ZIP Code</label><input className={inputCls} value={f.zip} onChange={set("zip")} data-testid="qf-zip" /></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Property Type</label>
          <select className={inputCls} value={f.property_type} onChange={set("property_type")} data-testid="qf-property-type">
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Service Requested</label>
          <select className={inputCls} value={f.service} onChange={set("service")} data-testid="qf-service">
            <option value="">Select a service…</option>
            {services.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {isCommercial && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Number of Locations</label>
            <input className={inputCls} value={f.property_count} onChange={set("property_count")} /></div>
          <div><label className={labelCls}>Recurring or One-Time</label>
            <select className={inputCls} value={f.recurring} onChange={set("recurring")}>
              <option value="">Select…</option><option>One-time</option><option>Recurring</option>
            </select></div>
        </div>
      )}

      <div>
        <label className={labelCls}>Property / Surface Type</label>
        <input className={inputCls} value={f.surface_type} onChange={set("surface_type")}
               placeholder="e.g. Driveway, pool deck, building exterior" data-testid="qf-surface" />
      </div>

      <div>
        <label className={labelCls}>Description of Work</label>
        <textarea className={`${inputCls} h-28 py-3`} value={f.description} onChange={set("description")}
                  placeholder="Tell us what you need cleaned…" data-testid="qf-description" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div><label className={labelCls}>Preferred Contact</label>
          <select className={inputCls} value={f.preferred_contact} onChange={set("preferred_contact")}>
            <option value="">Select…</option>{contactMethods.map((m) => <option key={m}>{m}</option>)}
          </select></div>
        <div><label className={labelCls}>Preferred Callback Date</label>
          <input type="date" className={inputCls} value={f.preferred_date} onChange={set("preferred_date")} /></div>
        <div><label className={labelCls}>Preferred Callback Time</label>
          <input className={inputCls} value={f.preferred_time} onChange={set("preferred_time")} placeholder="e.g. Mornings" /></div>
      </div>

      <div>
        <label className={labelCls}>How did you hear about us?</label>
        <select className={inputCls} value={f.heard_about} onChange={set("heard_about")}>
          <option value="">Select…</option>{heardOptions.map((h) => <option key={h}>{h}</option>)}
        </select>
      </div>

      {/* Photo Upload */}
      <div>
        <label className={labelCls}>Upload Photos (optional)</label>
        <label className="flex items-center justify-center gap-2 h-24 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[var(--action)] transition-colors text-[var(--text-muted)]">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          <span className="text-sm">{uploading ? "Uploading…" : "Tap to add photos of the area"}</span>
          <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={upload} data-testid="qf-photos" />
        </label>
        <p className="text-xs text-gray-400 mt-1">Your photos are private and only visible to the team.</p>
        {photos.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {photos.map((p, i) => (
              <span key={p.id} className="flex items-center gap-1.5 text-xs bg-[var(--surface)] px-3 py-1.5 rounded-full">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> {p.filename}
                <button type="button" onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className={labelCls}>Additional Notes (optional)</label>
        <textarea className={`${inputCls} h-20 py-3`} value={f.additional_notes} onChange={set("additional_notes")} />
      </div>

      <label className="flex items-start gap-3 text-sm text-[var(--text-muted)] cursor-pointer">
        <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })}
               className="mt-1 w-4 h-4 accent-[var(--action)]" data-testid="qf-consent" />
        <span>I consent to be contacted by Veterans Power Wash & Cleaning Services by phone, text, or email about my request. Message and data rates may apply.</span>
      </label>

      <FinancingNote />

      <button type="submit" disabled={submitting} data-testid="qf-submit"
        className="w-full inline-flex items-center justify-center gap-2 btn-lift bg-[var(--red)] hover:bg-[var(--red-dark)] text-white font-semibold px-6 py-3.5 rounded-full text-base disabled:opacity-60">
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Get My Free Quote"}
      </button>
      <p className="text-xs text-center text-gray-400">We'll review your request and follow up. No obligation.</p>
    </form>
  );
};
